'use client'

import { VoucherForm } from '@/components/vouchers/VoucherForm'
import { VoucherList } from '@/components/vouchers/VoucherList'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Voucher, VoucherFormData } from '@/types/api'
import React, { useCallback, useEffect, useState } from 'react'

const VouchersPage: React.FC = () => {
  const { toast } = useToast()
  const [vouchers, setVouchers] = useState<Voucher[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingVoucher, setEditingVoucher] = useState<Voucher | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')

  // Load vouchers with filters and pagination
  const loadVouchers = useCallback(
    async (page: number = 1, filters?: { search?: string; status?: string; type?: string }) => {
      setIsLoading(true)
      try {
        const params: Record<string, string | number> = {
          page,
          per_page: itemsPerPage,
        }

        if (filters?.search) {
          params.search = filters.search
        }

        if (filters?.status && filters.status !== 'all') {
          params.status = filters.status
        }

        if (filters?.type && filters.type !== 'all') {
          params.type = filters.type
        }

        const response = await apiClient.getVouchers(params)
        setVouchers(response.data || [])
        setTotalItems(response.meta?.total || 0)
        setTotalPages(response.meta?.last_page || 1)
        setCurrentPage(response.meta?.current_page || 1)
      } catch (error) {
        console.error('Error loading vouchers:', error)
        // Only show error toast if it's not a 404 (which might mean no vouchers found)
        if (error && typeof error === 'object' && 'status' in error && error.status !== 404) {
          toast({
            title: 'Error',
            description: 'Failed to load vouchers. Please try again.',
            variant: 'destructive',
          })
        }
        // Set empty state on error to prevent UI issues
        setVouchers([])
        setTotalItems(0)
        setTotalPages(1)
        setCurrentPage(1)
      } finally {
        setIsLoading(false)
      }
    },
    [itemsPerPage, toast]
  )

  // Effect to handle data loading on filter/search changes
  useEffect(() => {
    // If there is a search term, we need to debounce to avoid excessive requests handling typing
    if (searchTerm) {
      const timer = setTimeout(() => {
        setCurrentPage(1)
        loadVouchers(1, {
          search: searchTerm,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          type: typeFilter !== 'all' ? typeFilter : undefined,
        })
      }, 500)
      return () => clearTimeout(timer)
    }

    // For other filters or initial load (empty search), fetch immediately
    // We reset to page 1 for any filter change
    setCurrentPage(1)
    loadVouchers(1, {
      search: undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined,
    })
  }, [searchTerm, statusFilter, typeFilter, loadVouchers])

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
  }

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    loadVouchers(page, {
      search: searchTerm || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined,
    })
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const handleAddVoucher = () => {
    setEditingVoucher(null)
    setShowForm(true)
  }

  const handleEditVoucher = (voucher: Voucher) => {
    setEditingVoucher(voucher)
    setShowForm(true)
  }

  const handleFormSubmit = async (data: VoucherFormData) => {
    setIsSubmitting(true)
    try {
      if (editingVoucher) {
        await apiClient.updateVoucher(editingVoucher.id, data)
        toast({
          title: 'Success',
          description: 'Voucher updated successfully',
        })
      } else {
        await apiClient.createVoucher(data)
        toast({
          title: 'Success',
          description: 'Voucher created successfully',
        })
      }

      setShowForm(false)
      setEditingVoucher(null)
      loadVouchers(currentPage, {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
      })
    } catch {
      toast({
        title: 'Error',
        description: `Failed to ${editingVoucher ? 'update' : 'create'} voucher. Please try again.`,
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingVoucher(null)
  }

  const handleDeleteVoucher = async (id: string) => {
    try {
      await apiClient.deleteVoucher(id)
      toast({
        title: 'Success',
        description: 'Voucher deleted successfully',
      })
      loadVouchers(currentPage, {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete voucher. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleToggleStatus = async (id: string) => {
    try {
      await apiClient.toggleVoucherStatus(id)
      toast({
        title: 'Success',
        description: 'Voucher status updated successfully',
      })
      loadVouchers(currentPage, {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update voucher status. Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Vouchers</h1>
        <p className="text-muted-foreground">Manage discount vouchers and promotional codes</p>
      </div>

      {showForm ? (
        <VoucherForm
          voucher={editingVoucher}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={isSubmitting}
        />
      ) : (
        <VoucherList
          vouchers={vouchers}
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          typeFilter={typeFilter}
          onSearchChange={handleSearchChange}
          onStatusFilterChange={handleStatusFilterChange}
          onTypeFilterChange={handleTypeFilterChange}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAddVoucher={handleAddVoucher}
          onEditVoucher={handleEditVoucher}
          onDeleteVoucher={handleDeleteVoucher}
          onToggleStatus={handleToggleStatus}
          onRefresh={() =>
            loadVouchers(currentPage, {
              search: searchTerm || undefined,
              status: statusFilter !== 'all' ? statusFilter : undefined,
              type: typeFilter !== 'all' ? typeFilter : undefined,
            })
          }
        />
      )}
    </div>
  )
}

export default VouchersPage
